import { trackMenuNavigation } from '../menuAnalytics';
import { useMemo, useRef, useCallback, useState, useEffect } from 'react';
import * as translator from '../../../../utils/translator';
import { WhyChooseUsSection } from './WhyChooseUsSection';
import { ProductCarousel } from './ProductCarousel';
import { FoundayoMenuPromoWl } from './FoundayoMenuPromos';
import { getFavoriteProductCatalogIds } from '../../../../utils/favoritesStorage';
import { getRecentlyViewedProductCatalogIds } from '../../../../utils/recentlyViewedStorage';
import * as http from '../../../../utils/http';

interface FavProduct {
  catalog_id: number;
  title: string;
  asset: string;
  in_stock: boolean;
  cannabis_product_properties?: {
    genetic?: string;
    thc?: number | null;
    cbd?: number | null;
  } | null;
}

type TopProduct = {
  id: string;
  name: string;
  price: string;
  originalPrice?: string | null;
  discount?: number | null;
  image: string;
  url?: string;
};

interface CategoryDetailViewProps {
  category: SidebarTopCategory;
  onBack: () => void;
  language: string;
  country: string;
  isReturningPatient: boolean;
  isGloballyReturningPatient: boolean;
  otcTopProducts?: TopProduct[];
}

export function CategoryDetailView({
  category,
  onBack: _onBack,
  language,
  country,
  isReturningPatient,
  isGloballyReturningPatient,
  otcTopProducts,
}: CategoryDetailViewProps) {
  const assetBasePath = (window as any).clientConfig?.assetBasePath || 'https://images.doktorabc.com/redesign_2025';

  const getItemLabel = (item: SidebarTopCategoryItem): string => {
    if (item.label) return item.label;
    if (!item.label_key) return '';

    const translated = translator.translate(language, 'layout', `menu/submenu/${item.label_key}`);
    if (translated) {
      return translated.replace(':catalog_title', category.label || '');
    }
    return item.label_key;
  };

  // Hero card image: menu/{mention}/submenu_block_mobile/submenu_block_mobile.png
  const getHeroAssetUrl = (mention: string): string => {
    const actualMention = category.assetMention || mention;
    return `${assetBasePath}/menu/${actualMention}/submenu_block_mobile/submenu_block_mobile.png`;
  };

  const getHeroAssetUrlDesktop = (mention: string): string => {
    const actualMention = category.assetMention || mention;
    return `${assetBasePath}/menu/${actualMention}/submenu_block_desktop/submenu_block_desktop.png`;
  };

  // Submenu item image: general/{assetName}/{assetName}.png
  const getSubItemAssetUrl = (assetName: string): string => {
    return `${assetBasePath}/general/${assetName}/${assetName}.png`;
  };

  const handleItemClick = (item: SidebarTopCategoryItem, subLabel?: string, url?: string) => {
    const itemLabel = getItemLabel(item);
    const itemMixpanelLabel = subLabel || item.mixpanel_title || itemLabel;
    trackMenuNavigation({
      category: category.mixpanel_title || category.label,
      sub_category: itemMixpanelLabel,
      button_label: itemMixpanelLabel,
      carousel: 'no',
      link: url,
    });
  };

  const handleTreatmentCardClick = (productName: string, position: number, link?: string) => {
    trackMenuNavigation({
      category: category.mixpanel_title || category.label,
      carousel: 'yes',
      carousel_product_location: position + 1,
      carousel_product_name: productName,
      carousel_product_price: '',
      carousel_product_discount: '',
      button_label: productName,
      link,
    });
  };

  const handleGroupedItemClick = (subItem: any) => {
    trackMenuNavigation({
      category: category.mixpanel_title || category.label,
      sub_category: subItem.mixpanel_title || subItem.label,
      button_label: subItem.mixpanel_title || subItem.label,
      carousel: 'no',
      link: subItem.url,
    });
  };

  const handleTopProductClick = (product: TopProduct, index: number) => {
    trackMenuNavigation({
      category: category.mixpanel_title || category.label,
      carousel: 'yes',
      carousel_product_location: index + 1,
      carousel_product_name: product.name,
      carousel_product_price: product.originalPrice || product.price,
      carousel_product_discount: product.discount ? product.discount + '%' : '',
      button_label: product.name,
      link: product.url,
    });
  };

  const filteredItems = useMemo(() => {
    if (!category.items) return [];

    return category.items.filter(item => {
      if (!item.visibility || item.visibility === 'all') return true;
      if (item.visibility === 'ftb_only') return !isReturningPatient;
      if (item.visibility === 'rp_only') return isReturningPatient;
      return true;
    });
  }, [category.items, isReturningPatient]);

  const renderHeroCard = () => {
    if (category.isOtc) return null;
    if (isReturningPatient) return null;
    // GB WL: orange Foundayo S2 promo replaces green FTB hero
    if ((country || '').toUpperCase() === 'GB' && category.mention === 'weight_loss') return null;

    const mention = category.mention || '';
    const heroTitle = translator.translate(language, 'layout', 'menu/submenu/start_treatment')?.replace(':catalog_title', category.label)
      || `Start ${category.label} Treatment`;

    return (
      <a
        href={category.consultationUrl || '#'}
        className="ftb-hero-card block relative mx-3 mt-2 h-[11rem] rounded-[1.875rem] overflow-hidden no-underline"
        style={{ background: category.gradient || 'linear-gradient(264deg, #8ACB88 11%, #57B17A 88%)' }}
        onClick={() => {
          trackMenuNavigation({
            category: category.mixpanel_title || category.label,
            button_label: heroTitle,
            sub_category: '',
            carousel: 'no',
            link: category.consultationUrl || undefined,
          });
        }}
      >
        <div className="flex h-full">
          <div className="relative z-10 flex flex-col justify-between p-5 flex-1">
            <span
              className="text-lg font-medium font-poppins leading-snug max-w-[12rem]"
              style={{ color: category.widget_text_color === 'black' ? '#000000' : '#ffffff' }}
            >
              {heroTitle}
            </span>
            <div className="bg-[#f5f6f7] border border-white rounded-full flex items-center justify-center w-[2.875rem] h-[2.875rem]">
              <img src="/img_v4/svg/icons/from_figma/arrow-icon.svg" alt="" className="w-5 h-5" />
            </div>
          </div>
          <div className="absolute top-0 right-0 h-full z-0 pointer-events-none">
            <img
              src={getHeroAssetUrl(mention)}
              alt=""
              className="h-full w-auto md:hidden block"
            />
            <img
              src={getHeroAssetUrlDesktop(mention)}
              alt=""
              className="h-full w-auto hidden md:block"
            />
          </div>
        </div>
      </a>
    );
  };

  const renderSingleOrFilterItem = (item: SidebarTopCategoryItem) => {
    const itemLabel = getItemLabel(item);
    const hasAsset = !!item.assetName;

    if (hasAsset) {
      // GB: Foundayo-branded tablets art; same general/{name}/{name}.png layout
      const resolvedAssetName =
        (country || '').toUpperCase() === 'GB' && item.assetName === 'weight_loss_menu_glp1_pill'
          ? 'weight_loss_menu_glp1_foundayo_pill'
          : item.assetName!;
      const isFoundayoPill = resolvedAssetName === 'weight_loss_menu_glp1_foundayo_pill';
      const isGlp1Pill =
        resolvedAssetName === 'weight_loss_menu_glp1_pill' || isFoundayoPill;
      const isGlp1Pen = resolvedAssetName === 'weight_loss_menu_glp1_pen';

      return (
        <a
          key={item.label_key || itemLabel}
          href={item.url}
          className="submenu-pill-with-asset mx-3 mt-[9px] flex items-center h-[4rem] bg-[#f2f4f6] rounded-[1.25rem] overflow-hidden no-underline cursor-pointer hover:bg-[#eaecef] first:mt-4"
          style={{ transition: 'background-color 0.2s ease', textDecoration: 'none' }}
          onClick={() => handleItemClick(item, undefined, item.url)}
        >
          <div
            className={`submenu-pill-asset-container h-full flex shrink-0 overflow-hidden${
              isGlp1Pill
                ? ' w-[4.25rem] items-center justify-center'
                : isGlp1Pen
                  ? ' w-[4.25rem] items-end justify-start pr-2'
                  : ' w-[4.25rem] items-end justify-start'
            }`}
          >
            <img
              src={getSubItemAssetUrl(resolvedAssetName)}
              alt=""
              className={
                isFoundayoPill
                  ? 'submenu-foundayo-pill-asset'
                  : `h-full w-auto object-contain drop-shadow-[0px_4px_8px_rgba(0,0,0,0.1)]${
                      isGlp1Pen
                        ? ' object-bottom ml-[-0.75rem]'
                        : isGlp1Pill
                          ? ' object-center'
                          : ' object-bottom'
                    }`
              }
            />
          </div>
          <span className="flex-1 min-w-0 text-[16px] text-[#1f2937] font-poppins font-medium leading-[21px] tracking-normal">{itemLabel}</span>
          {(item.withNewTag || item.withNewTagFromMenu) && (
            <span className="text-xs linear-background-color-primary text-white p-1 rounded-[5px] font-semibold shrink-0 mr-2">
              {translator.translate(language, 'layout', 'sidebar/new_tag')}
            </span>
          )}
          <div className="pr-4 shrink-0">
            <img src="/img_v4/icons/chevron-right-icon.svg" alt="" aria-hidden="true" className="w-[5.33px] h-[9.33px]" style={{ color: '#98a0af' }} />
          </div>
        </a>
      );
    }

    return (
      <a
        key={item.label_key || itemLabel}
        href={item.url}
        className="mx-3 mt-[9px] flex items-center justify-between h-[4rem] bg-[#f2f4f6] rounded-[1.25rem] px-4 no-underline cursor-pointer hover:bg-[#eaecef] first:mt-4"
        style={{ transition: 'background-color 0.2s ease', textDecoration: 'none' }}
        onClick={() => handleItemClick(item, undefined, item.url)}
      >
        <span className="flex-1 min-w-0 text-[16px] text-[#1f2937] font-poppins font-medium leading-[21px] tracking-normal">{itemLabel}</span>
        {(item.withNewTag || item.withNewTagFromMenu) && (
          <span className="text-xs linear-background-color-primary text-white p-1 rounded-[5px] font-semibold shrink-0 mr-2">
            {translator.translate(language, 'layout', 'sidebar/new_tag')}
          </span>
        )}
        <img src="/img_v4/icons/chevron-right-icon.svg" alt="" aria-hidden="true" className="w-[5.33px] h-[9.33px]" style={{ color: '#98a0af' }} />
      </a>
    );
  };

  const renderTreatmentsBlock = (item: SidebarTopCategoryItem) => {
    if (!item.products || item.products.length === 0) return null;
    const itemLabel = getItemLabel(item);

    return (
      <div key={item.label_key || itemLabel} className="mt-[9px] mx-3 bg-[#f2f4f6] rounded-[20px]">
        <span className="block text-[13px] md:text-[15px] font-medium text-[#757575] uppercase tracking-[1px] leading-[26px] font-poppins px-5 md:px-6 pt-4 pb-2">{category.label}</span>
        <div className="flex flex-col pb-2">
          {item.products.map((product) => (
            <a
              key={product.title}
              href={product.url}
              className="flex items-center justify-between w-full text-left px-5 md:px-6 h-[3.375rem] bg-transparent border-none cursor-pointer no-underline hover:bg-[#eaecef] rounded-[10px]"
              style={{ transition: 'background-color 0.2s ease' }}
              onClick={() => handleItemClick(item, product.title, product.url)}
            >
              <div className="flex items-start gap-[2px] flex-1 min-w-0">
                <span className="text-[16px] text-[#1f2937] font-poppins font-medium leading-[21px] tracking-normal">{product.title}</span>
                {product.isRx !== false && (
                  <span className="text-[10px] text-[#269c6e] font-medium leading-none -mt-0.5">Rx</span>
                )}
              </div>
              <img src="/img_v4/icons/chevron-right-icon.svg" alt="" aria-hidden="true" className="w-[5.33px] h-[9.33px] shrink-0 ml-2" style={{ color: '#98a0af' }} />
            </a>
          ))}
        </div>
      </div>
    );
  };

  const getSymptomLabel = (symptom: { label?: string; label_key?: string }): string => {
    if (symptom.label) return symptom.label;
    if (!symptom.label_key) return '';

    const translated = translator.translate(language, 'layout', `menu/submenu/${symptom.label_key}`);
    return translated || symptom.label_key;
  };

  const renderSymptomTreatmentsBlock = (item: SidebarTopCategoryItem) => {
    if (!item.symptoms || item.symptoms.length === 0) return null;
    const itemLabel = getItemLabel(item);

    return (
      <div key={item.label_key || itemLabel} className="mt-[9px] mx-3">
        <span className="block text-[13px] md:text-[15px] font-medium text-[#757575] uppercase tracking-[1px] leading-[26px] mb-2 font-poppins pt-3">{itemLabel}</span>
        <div className="flex flex-col gap-[9px]">
          {item.symptoms.map((symptom) => {
            const symptomLabel = getSymptomLabel(symptom);
            return (
              <a
                key={symptom.label || symptom.label_key}
                href={symptom.url}
                className="flex items-center justify-between h-[4rem] bg-[#f2f4f6] rounded-[1.25rem] px-4 no-underline cursor-pointer hover:bg-[#eaecef]"
                style={{ transition: 'background-color 0.2s ease' }}
                onClick={() => handleItemClick(item, symptomLabel, symptom.url)}
              >
                <span className="flex-1 min-w-0 text-[16px] text-[#1f2937] font-poppins font-medium leading-[21px] tracking-normal">{symptomLabel}</span>
                <img src="/img_v4/icons/chevron-right-icon.svg" alt="" aria-hidden="true" className="w-[5.33px] h-[9.33px] shrink-0" style={{ color: '#98a0af' }} />
              </a>
            );
          })}
        </div>
      </div>
    );
  };

  const [favProducts, setFavProducts] = useState<FavProduct[] | null>(null);
  const [isFavLoading, setIsFavLoading] = useState(false);

  useEffect(() => {
    const hasFavoritesCarousel = filteredItems.some(
      item => item.template === 'treatments_cards' && item.treatmentCards?.type === 'favorites'
    );
    if (!hasFavoritesCarousel) return;

    const favIds = getFavoriteProductCatalogIds().slice(0, 20);
    const rvIds = getRecentlyViewedProductCatalogIds();
    const favSet = new Set(favIds);
    const rvFiltered = rvIds.filter(id => !favSet.has(id)).slice(0, 20 - favIds.length);
    const catalogIds = [...favIds, ...rvFiltered];
    if (!catalogIds.length) return;

    setIsFavLoading(true);
    http.postToServer(`products-by-catalog-ids/${language}/${country}`, { catalogIds })
      .then((res: any) => { setFavProducts(res.data?.products || []); })
      .catch(() => { setFavProducts([]); })
      .finally(() => { setIsFavLoading(false); });
  }, []);

  const carouselRef = useRef<HTMLDivElement>(null);
  const dragState = useRef({ isDragging: false, startX: 0, scrollStart: 0, wasDragging: false });
  const favCarouselRef = useRef<HTMLDivElement>(null);
  const favDragState = useRef({ isDragging: false, startX: 0, scrollStart: 0, wasDragging: false });
  const DRAG_THRESHOLD = 5;

  const onCarouselMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    const state = dragState.current;
    const el = carouselRef.current;
    if (!el) return;

    state.isDragging = true;
    state.wasDragging = false;
    state.startX = e.clientX;
    state.scrollStart = el.scrollLeft;
    el.style.cursor = 'grabbing';
    el.style.userSelect = 'none';

    const onMouseMove = (ev: MouseEvent) => {
      if (!state.isDragging) return;
      const walk = ev.clientX - state.startX;
      if (Math.abs(walk) > DRAG_THRESHOLD) state.wasDragging = true;
      el.scrollLeft = state.scrollStart - walk;
    };

    const onMouseUp = () => {
      state.isDragging = false;
      el.style.cursor = '';
      el.style.userSelect = '';
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, []);

  const onCarouselClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (dragState.current.wasDragging) {
      e.preventDefault();
      e.stopPropagation();
      dragState.current.wasDragging = false;
    }
  }, []);

  const onFavCarouselMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    const state = favDragState.current;
    const el = favCarouselRef.current;
    if (!el) return;

    state.isDragging = true;
    state.wasDragging = false;
    state.startX = e.clientX;
    state.scrollStart = el.scrollLeft;
    el.style.cursor = 'grabbing';
    el.style.userSelect = 'none';

    const onMouseMove = (ev: MouseEvent) => {
      if (!state.isDragging) return;
      const walk = ev.clientX - state.startX;
      if (Math.abs(walk) > DRAG_THRESHOLD) state.wasDragging = true;
      el.scrollLeft = state.scrollStart - walk;
    };

    const onMouseUp = () => {
      state.isDragging = false;
      el.style.cursor = '';
      el.style.userSelect = '';
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };

    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, []);

  const onFavCarouselClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (favDragState.current.wasDragging) {
      e.preventDefault();
      e.stopPropagation();
      favDragState.current.wasDragging = false;
    }
  }, []);

  const renderTreatmentCards = (item: SidebarTopCategoryItem) => {
    if (!item.treatmentCards) return null;

    const { type, products } = item.treatmentCards;
    const itemLabel = getItemLabel(item);
    const inStockLabel = translator.translate(language, 'layout', 'sidebar/in_stock');

    if (type === 'favorites') {
      if (!isFavLoading && (!favProducts || favProducts.length === 0)) return null;



      return (
        <div key={item.label_key || itemLabel} className="mt-[9px] mx-3 bg-[#f2f4f6] rounded-[1.25rem] pb-4">
          <span className="block text-[0.8125rem] font-medium text-[#757575] uppercase tracking-[1px] font-poppins px-5 pt-4 pb-3">{itemLabel}</span>
          {isFavLoading ? (
            <div className="flex gap-[14px] overflow-x-auto px-3 pb-1" style={{ scrollbarWidth: 'none' }}>
              {[0, 1, 2].map(i => (
                <div key={i} className="shrink-0 w-[10rem] bg-white rounded-[1.25rem] overflow-hidden animate-pulse">
                  <div className="h-[5.5rem] bg-gray-200" />
                  <div className="px-3 py-3">
                    <div className="h-3 bg-gray-200 rounded mb-2 w-3/4" />
                    <div className="h-3 bg-gray-200 rounded w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div
              ref={favCarouselRef}
              className="flex gap-[14px] overflow-x-auto px-3 pb-1 cursor-grab"
              style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}
              onMouseDown={onFavCarouselMouseDown}
              onClick={onFavCarouselClick}
            >
              {(favProducts || []).map((product, index) => {
                const sep = item.url.includes('?') ? '&' : '?';
                const productUrl = `${item.url}${sep}catalog_id=${product.catalog_id}`;
                const imageUrl = product.asset || null;
                const props = product.cannabis_product_properties;
                return (
                  <a
                    key={product.catalog_id}
                    href={productUrl}
                    className="shrink-0 w-[10rem] bg-white rounded-[1.25rem] overflow-hidden no-underline block shadow-[0px_4px_8px_rgba(0,0,0,0.08)]"
                    onClick={() => handleTreatmentCardClick(product.title, index, productUrl)}
                  >
                    <div className="h-[6.5rem] flex items-center justify-center overflow-hidden">
                      {imageUrl ? (
                        <img src={imageUrl} alt={product.title} className="h-[5.5rem] w-auto object-contain" />
                      ) : (
                        <img src="/img_v2025/side-menu-category-placeholder.png" alt="" className="h-[5.5rem] w-auto object-contain" />
                      )}
                    </div>
                    <div className="px-3 pb-3">
                      {props && (props.genetic || props.thc != null || props.cbd != null) && (
                        <div className="flex flex-wrap gap-1 mb-2">
                          {props.genetic && <span className="bg-[#e9fbeb] text-[#02a414] text-xs px-1 py-px rounded-[6px]">{props.genetic}</span>}
                          {props.thc != null && <span className="bg-[#e9fbeb] text-[#02a414] text-xs px-1 py-px rounded-[6px]">THC: {props.thc}%</span>}
                          {props.cbd != null && <span className="bg-[#e9fbeb] text-[#02a414] text-xs px-1 py-px rounded-[6px]">CBD: {props.cbd}%</span>}
                        </div>
                      )}
                      <p className="text-xs font-semibold text-black font-poppins leading-[1.3] m-0 line-clamp-2">{product.title}</p>
                      <div className="flex items-center justify-between mt-2">
                        {product.in_stock && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full bg-[#e9fbeb] text-[#02a414] text-[10px] font-medium">
                            {inStockLabel}
                          </span>
                        )}
                        <span className="w-8 h-8 rounded-full bg-[#f2f4f6] flex items-center justify-center shrink-0 ml-auto">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#222" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M5 12h14M12 5l7 7-7 7" />
                          </svg>
                        </span>
                      </div>
                    </div>
                  </a>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    if (type === 'specific_products' && products && products.length > 0) {
      return (
        <div key={item.label_key || itemLabel} className="mt-[9px] mx-3 bg-[#f2f4f6] rounded-[1.25rem] pb-4">
          <span className="block text-[0.8125rem] font-medium text-[#757575] uppercase tracking-[1px] font-poppins px-5 pt-4 pb-3">{itemLabel}</span>
          <div
            ref={carouselRef}
            className="sidemenu-treatments-carousel flex gap-[14px] overflow-x-auto px-3 pb-1 cursor-grab"
            style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}
            onMouseDown={onCarouselMouseDown}
            onClick={onCarouselClick}
          >
            {products.map((product, index) => (
              <a
                key={product.catalogId}
                href={product.url}
                className="shrink-0 w-[10rem] bg-white rounded-[1.25rem] overflow-hidden no-underline block shadow-[0px_4px_8px_rgba(0,0,0,0.08)]"
                onClick={() => handleTreatmentCardClick(product.title, index, product.url)}
              >
                <div className="h-[6.5rem] flex items-center justify-center overflow-hidden">
                  {product.image ? (
                    <img src={product.image} alt={product.title} className="h-[5.5rem] w-auto object-contain" />
                  ) : (
                    <img src="/img_v2025/side-menu-category-placeholder.png" alt="" className="h-[5.5rem] w-auto object-contain" />
                  )}
                </div>
                <div className="px-3 pb-3">
                  <p className="text-xs font-semibold text-black font-poppins leading-[1.3] m-0 line-clamp-2">{product.title}</p>
                  <div className="flex items-center justify-between mt-2">
                    {product.inStock && (
                      <span className="inline-flex items-center px-2 py-1 rounded-full bg-[#e9fbeb] text-[#02a414] text-[10px] font-medium">
                        {inStockLabel}
                      </span>
                    )}
                    <span className="w-8 h-8 rounded-full bg-[#f2f4f6] flex items-center justify-center shrink-0 ml-auto">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#222" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 12h14M12 5l7 7-7 7" />
                      </svg>
                    </span>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      );
    }

    return null;
  };

  const getTranslatedGroupLabel = (groupLabel: string): string => {
    // groupLabel is a translation key like 'front_v25/sidebar.menu.otc_group.family_personal_care'
    // Convert to translator format: 'sidebar.menu.otc_group.family_personal_care'
    const key = groupLabel.replace('front_v25/', '');
    const translated = translator.translate(language, 'layout', key);
    return translated || groupLabel.split('.').pop()?.replace(/_/g, ' ') || groupLabel;
  };

  const renderOtcGroupedItems = () => {
    if (!category.groupedItems || category.groupedItems.length === 0) return null;

    return category.groupedItems.map((group, groupIdx) => (
      <div key={group.groupLabel} className={groupIdx > 0 ? 'mt-6' : ''}>
        <p className="font-poppins font-medium text-[13px] text-[#757575] uppercase tracking-[1px] leading-[26px] mb-2 mt-4 px-3">
          {getTranslatedGroupLabel(group.groupLabel)}
        </p>
        <div className="flex flex-col gap-[9px] px-3">
          {group.items.map((subItem) => (
            <a
              key={subItem.label}
              href={subItem.url}
              className="flex items-center justify-between h-[4rem] bg-[#f2f4f6] rounded-[1.25rem] px-4 cursor-pointer hover:bg-[#eaecef] no-underline"
              style={{ transition: 'background-color 0.2s ease' }}
              onClick={() => handleGroupedItemClick(subItem)}
            >
              <span className="text-[16px] text-[#1f2937] font-poppins font-medium leading-[21px] tracking-normal">{subItem.label}</span>
              <img src="/img_v4/icons/chevron-right-icon.svg" alt="" aria-hidden="true" className="w-[5.33px] h-[9.33px]" style={{ color: '#98a0af' }} />
            </a>
          ))}
        </div>
      </div>
    ));
  };

  const handleRootCatalogChildClick = (child: SidebarTopCategoryItem) => {
    trackMenuNavigation({
      category: category.mixpanel_title || category.label,
      sub_category: child.mixpanel_title || child.label,
      button_label: child.label,
      carousel: 'no',
      link: child.url,
    });
    if (child.url) {
      window.location.href = child.url;
    }
  };

  // Root catalog sub menu: simple titled list of children (master/production design).
  if (category.isRootCatalog) {
    return (
      <>
        <div id="submenu-main-section" className="side-menu-section first-section side-menu-submenu-main">
          <h2 className="side-menu-submenu-title">{category.label}</h2>
          <div className="side-menu-items" id="submenu-items">
            {(category.items || []).map((child) => (
              <button
                key={child.url || child.label}
                className="side-menu-item-btn"
                type="button"
                onClick={() => handleRootCatalogChildClick(child)}
                data-category={category.mixpanel_title || category.label}
                data-button-label={child.label}
              >
                <div className="side-menu-submenu-item">
                  <span className="side-menu-item-text">{child.label}</span>
                  {child.sticker && <span className="side-menu-new-badge">{child.sticker}</span>}
                </div>
                <img
                  src="/img_v4/icons/chevron-right-icon.svg"
                  alt=""
                  aria-hidden="true"
                  className="side-menu-item-arrow"
                  style={{ width: '5.33px', height: '9.33px' }}
                />
              </button>
            ))}
          </div>
        </div>

        {!isGloballyReturningPatient && (
          <WhyChooseUsSection language={language} country={country} />
        )}
      </>
    );
  }

  return (
    <div id={`submenu-${category.mention}`} className="pb-4">
      {category.isOtc ? (
        <>
          <h2 className="side-menu-submenu-title px-3">{category.label}</h2>
          {renderOtcGroupedItems()}
          {otcTopProducts && otcTopProducts.length > 0 && (
            <div id="otc-top-products-section" className="mt-5 px-3 bg-[#f2f4f6] rounded-[1.25rem] pb-4">
              <span className="block text-[0.8125rem] font-medium text-[#757575] uppercase tracking-[1px] font-poppins px-8 pt-5 pb-3">
                {translator.translate(language, "my_account", "SideMenu/top_product")}
              </span>
              <ProductCarousel
                products={otcTopProducts}
                onProductClick={handleTopProductClick}
              />
            </div>
          )}
        </>
      ) : (
        <div id={`submenu-items`}>
          {renderHeroCard()}

          {(country || '').toUpperCase() === 'GB' && category.mention === 'weight_loss' && (
            <FoundayoMenuPromoWl
              language={language}
              categoryUrl={category.url}
              trackingCategory={category.mixpanel_title || category.label || 'weight_loss'}
            />
          )}

          {filteredItems.map((item) => {
            if (item.template === 'single' || item.template === 'filter') {
              return renderSingleOrFilterItem(item);
            } else if (item.template === 'treatments_block') {
              return renderTreatmentsBlock(item);
            } else if (item.template === 'symptom_treatments_block') {
              return renderSymptomTreatmentsBlock(item);
            } else if (item.template === 'treatments_cards') {
              return renderTreatmentCards(item);
            }
            return null;
          })}
        </div>
      )}

      {!isGloballyReturningPatient && (
        <WhyChooseUsSection language={language} country={country} />
      )}
    </div>
  );
}
